// Centralized file URL helper for all external API resources
// Handles both Cloudinary URLs (complete) and legacy local URLs

// #Fase0-B5 — REACT_APP_API_URL trae el sufijo /api (ver .env.example y
// .do/app.yaml), pero aquí se usa como ORIGEN: las rutas de abajo ya
// agregan "/api/...". Sin quitarlo, cualquier URL relativa vieja quedaba
// como ".../api/api/evidence/..." (404). Las URL absolutas (Cloudinary) se
// devuelven tal cual, así que esto solo afecta a registros viejos.
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.soempg.com').replace(/\/api\/?$/, '');

/**
 * Resolve any file URL - handles Cloudinary and legacy URLs
 * @param {string} url - URL or filename (can be Cloudinary full URL or legacy path)
 * @param {string} type - Type: 'evidence', 'staff', 'client', 'location', 'user'
 * @param {string} userId - Optional user ID for user photos
 * @returns {string|null}
 */
export const resolveFileUrl = (url, type = 'evidence', userId = null) => {
  if (!url) return null;
  
  // If URL is already complete (Cloudinary or any http URL), return as-is
  if (url.startsWith('http')) return url;
  
  // Handle legacy URLs that start with /api/ or /uploads/
  if (url.startsWith('/api/') || url.startsWith('/uploads/')) {
    return `${API_BASE}${url}`;
  }
  
  // Build legacy URL based on type (fallback for old filename-only entries)
  switch (type) {
    case 'evidence':
      return `${API_BASE}/api/evidence/${url.replace(/^\//, '')}`;
    case 'staff':
      return `${API_BASE}/api/staff/photos/${url.replace(/^\//, '')}`;
    case 'client':
      return `${API_BASE}/api/clients/logos/${url.replace(/^\//, '')}`;
    case 'location':
      return `${API_BASE}/api/locations/photos/${url.replace(/^\//, '')}`;
    case 'user':
      return userId 
        ? `${API_BASE}/api/users/${userId}/photo/${url.replace(/^\//, '')}`
        : `${API_BASE}/api/users/photo/${url.replace(/^\//, '')}`;
    default:
      return `${API_BASE}/${url.replace(/^\//, '')}`;
  }
};

/**
 * Get URL for evaluation evidence files
 * @param {string} url - Full Cloudinary URL or legacy path
 * @returns {string|null}
 */
/**
 * Pide a Cloudinary un formato que el navegador pueda mostrar.
 * Las fotos de galería de iPhone llegan como HEIC y Cloudinary las guarda tal
 * cual; Chrome no muestra HEIC, así que la miniatura caía al "?" de SafeImage.
 * Solo toca imágenes públicas de Cloudinary sin formato ya pedido; las URLs
 * firmadas no se modifican porque la firma cubre la transformación.
 * @param {string} url
 * @param {number} [maxWidth] - ancho máximo para miniaturas (evita bajar fotos de 1-2 MB)
 * @returns {string}
 */
export const viewableImageUrl = (url, maxWidth) => {
  if (typeof url !== 'string' || !url.startsWith('https://res.cloudinary.com/')) return url;
  const marker = '/image/upload/';
  const at = url.indexOf(marker);
  if (at < 0) return url;
  const rest = url.slice(at + marker.length);
  const firstSegment = rest.split('/')[0];
  if (firstSegment.startsWith('s--') || /(^|,)f_[^,]+/.test(firstSegment)) return url;
  const size = maxWidth ? `,w_${maxWidth},c_limit` : '';
  return `${url.slice(0, at + marker.length)}f_auto,q_auto${size}/${rest}`;
};

export const getEvidenceUrl = (url) => viewableImageUrl(resolveFileUrl(url, 'evidence'));

/**
 * Get URL for staff photos
 * @param {string} url - Full Cloudinary URL or legacy path
 * @returns {string|null}
 */
export const getStaffPhotoUrl = (url) => resolveFileUrl(url, 'staff');

/**
 * Get URL for client logos
 * @param {string} url - Full Cloudinary URL or legacy path
 * @returns {string|null}
 */
export const getClientLogoUrl = (url) => resolveFileUrl(url, 'client');

/**
 * Get URL for location photos
 * @param {string} url - Full Cloudinary URL or legacy path
 * @returns {string|null}
 */
export const getLocationPhotoUrl = (url) => resolveFileUrl(url, 'location');

/**
 * Get URL for user profile photos
 * @param {string} url - Full Cloudinary URL or legacy path
 * @param {string} userId - User ID (for legacy URLs)
 * @returns {string|null}
 */
export const getUserPhotoUrl = (url, userId) => resolveFileUrl(url, 'user', userId);

export default {
  resolveFileUrl,
  getEvidenceUrl,
  getStaffPhotoUrl,
  getClientLogoUrl,
  getLocationPhotoUrl,
  getUserPhotoUrl,
  API_BASE,
};
